import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createKitchenDining } from "../../../redux/actions/kitchenDiningActions";
import { useLanguage } from "../../../context/LanguageContext";

const SectionBQualityQuantity = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();

  /* ================================
     FORM STATE
  ================================ */
  const [form, setForm] = useState({
    b1: "",
    b2: "",
    b3: "",
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    
    // Clear error when user makes a selection
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required field validations
    if (!form.b1) newErrors.b1 = t.global.err_b1;
    if (!form.b2) newErrors.b2 = t.global.err_b2;
    if (!form.b3) newErrors.b3 = t.global.err_b3;
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ================================
     SAVE TO LOCAL STORAGE
  ================================ */
  // const handleSave = () => {
  //   if (!validateForm()) {
  //     // alert("Please fill in all required fields before saving.");
  //     return;
  //   }

  //   try {
  //     // Save form data to localStorage
  //     const surveyData = JSON.parse(localStorage.getItem("surveyData") || "{}");
  //     surveyData.sectionB = form;
  //     localStorage.setItem("surveyData", JSON.stringify(surveyData));

  //     // Mark Section B as completed
  //     const completedSections = JSON.parse(localStorage.getItem("completedSections") || "{}");
  //     completedSections.b = true;
  //     localStorage.setItem("completedSections", JSON.stringify(completedSections));

  //     // Navigate back to sections
  //     navigate("/survey/sections", { state });
      
  //     // Show success message
  //     alert("Section B saved successfully!");
  //   } catch (err) {
  //     console.error("Error saving data:", err);
  //     alert("Error saving data. Please try again.");
  //   }
  // };
  
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      // Get latitude & longitude automatically
      let latitude = "";
      let longitude = "";

      if (navigator.geolocation) {
        await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              latitude = position.coords.latitude;
              longitude = position.coords.longitude;
              resolve();
            },
            () => resolve() // proceed even if geolocation fails
          );
        });
      }

      // Map Section B values
      const ratingMap = { 
        [t.global.rating_good]: "1", 
        [t.global.rating_satisfactory]: "2", 
        [t.global.rating_poor]: "3" 
      };
      const yesNoMap = { [t.global.yes]: "1", [t.global.no]: "2" };

      const fd = new FormData();
      fd.append("district_id", state.district_id);
      fd.append("municipality_id", state.municipality_id);
      fd.append("ward_id", state.ward_id);
      fd.append("area_id", state.area_id);
      fd.append("canteen_type_id", state.canteen_type_id);
      fd.append("latitude", latitude || "0");
      fd.append("longitude", longitude || "0");
      fd.append("reporting_date", new Date().toISOString().split("T")[0])
      fd.append("quali_quanti_asment_B1", ratingMap[form.b1]);
      fd.append("quali_quanti_asment_B2", ratingMap[form.b2]);
      fd.append("quali_quanti_asment_B3", yesNoMap[form.b3]);
      fd.append("current_section", "assessment");

      // Dispatch API call
      const response = await dispatch(createKitchenDining(fd));

      if (response) {
        // ✅ Save Section B locally
        const surveyData = JSON.parse(localStorage.getItem("surveyData") || "{}");
        surveyData.sectionB = form;
        localStorage.setItem("surveyData", JSON.stringify(surveyData));

        const completedSections = JSON.parse(localStorage.getItem("completedSections") || "{}");
        completedSections.b = true;
        localStorage.setItem("completedSections", JSON.stringify(completedSections));

        // alert("Section B submitted successfully!");
        navigate("/survey/sections", { state });
      }

    } catch (err) {
      console.error("Error submitting Section B:", err);
      alert(t.global.alert_server_error);
    }
  };
  
  const ratingOptions = [t.global.rating_good, t.global.rating_satisfactory, t.global.rating_poor];

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t.modules.kitchenCumDining.sections.B.title}
              </h2>
              <p className="text-gray-600">
                {t.modules.kitchenCumDining.sections.B.subtitle}
              </p>
            </div>
            <div className="text-sm bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full">
              {state?.canteenId ? `${t.global.canteen_id_label} ${state.canteenId}` : 
               state?.kioskId ? `${t.global.canteen_id_label} ${state.kioskId}` : t.global.na}
            </div>
          </div>

          <div className="space-y-6">

            {/* B1 */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3">
                B1. {t.modules.kitchenCumDining.sections.B.questions.quality} <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {ratingOptions.map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-3 border rounded cursor-pointer ${
                      form.b1 === option ? 'bg-blue-50 border-blue-300' : ''
                    } ${errors.b1 ? 'border-red-300' : ''}`}
                  >
                    <input
                      type="radio"
                      name="b1"
                      value={option}
                      checked={form.b1 === option}
                      onChange={handleChange}
                      className="mr-3"
                      required
                    />
                    <span className="flex items-center">
                      <span className="mr-2">☐</span> {option}
                    </span>
                  </label>
                ))}
              </div>
              {errors.b1 && <p className="text-red-500 text-sm mt-2">{errors.b1}</p>}
            </div>

            {/* B2 */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3">
                B2. {t.modules.kitchenCumDining.sections.B.questions.quantity} <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {ratingOptions.map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-3 border rounded cursor-pointer ${
                      form.b2 === option ? 'bg-blue-50 border-blue-300' : ''
                    } ${errors.b2 ? 'border-red-300' : ''}`}
                  >
                    <input
                      type="radio"
                      name="b2"
                      value={option}
                      checked={form.b2 === option}
                      onChange={handleChange}
                      className="mr-3"
                      required
                    />
                    <span className="flex items-center">
                      <span className="mr-2">☐</span> {option}
                    </span>
                  </label>
                ))}
              </div>
              {errors.b2 && <p className="text-red-500 text-sm mt-2">{errors.b2}</p>}
            </div>

            {/* B3 */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3">
                B3. {t.modules.kitchenCumDining.sections.B.questions.items} <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-3 border rounded cursor-pointer ${
                      form.b3 === option ? 'bg-blue-50 border-blue-300' : ''
                    } ${errors.b3 ? 'border-red-300' : ''}`}
                  >
                    <input
                      type="radio"
                      name="b3"
                      value={option}
                      checked={form.b3 === option}
                      onChange={handleChange}
                      className="mr-3"
                      required
                    />
                    {option}
                  </label>
                ))}
              </div>
              {errors.b3 && <p className="text-red-500 text-sm mt-2">{errors.b3}</p>}
            </div>

            {/* Required fields note */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    <span className="text-red-500">*</span> {t.global.err_complete_all}
                  </p>
                </div>
              </div>
            </div>

            {/* ACTIONS */}
            <div className="flex justify-between pt-6 border-t">
              <button
                onClick={() => navigate("/survey/sections", { state })}
                className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition"
              >
                {t.global.back_to_sections}
              </button>

              <button
                onClick={handleSubmit}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                {t.global.submit_section_b}
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionBQualityQuantity;